// @ts-nocheck
import React, { useCallback, useEffect, useState } from "react";
import { Autocomplete, TextField } from "@mui/material";
import { useStoreActions, useStoreState } from "easy-peasy";
import FullPageLoader from "components/Loader/FullPage";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import CustomModal from "components/CustomModal";
import { get, post } from "requests";
import StorageUtils from "utils/StorageUtils";
import OcrolusTransactions from "./ocrolusTransactions";
import OcrolusSummaryCard from "./OcrolusSummaryCard";
import OcrolusStatementsSummary from "./OcrolusStatementsSummary";
import OcrolusAdvancesPositions from "./OcrolousAdvancesPositions";
import OcrolusRecurringPayments from "./OcrolusRecurringPayments";
import OcrolusDetectSignals from "./OcrolusDetectSignals";

type OcrolusStatus = "loading" | "not_found" | "processing" | "success" | "error";

export default function OcrolusTab({ dealApplicationId }) {
  const mediaAttachments = useStoreState((state) => state.fileUploads.mediaAttachments);

  const [files, setFiles] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [showModal, setShowModal] = useState(false);

  const [status, setStatus] = useState<OcrolusStatus>("loading");
  const [message, setMessage] = useState("");
  const [isRunning, setIsRunning] = useState(false);

  const [transactions, setTransactions] = useState([]);
  const [bankAccounts, setBankAccounts] = useState({});

  const [availableTags, setAvailableTags] = useState([]);
  const [tagsLoading, setTagsLoading] = useState(false);
  const [debitsSummary, setDebitsSummary] = useState({});
  const [ocrolusSummary, setOcrolusSummary] = useState<any>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [transactionsLoading, setTransactionsLoading] = useState(false);

  const setPositionCount = useStoreActions(
    (actions: any) => actions.bankSummaryStore.setPositionCount
  );

  const [loadingStates, setLoadingStates] = useState<Record<string, boolean>>({
    offers: false,
    dealPayoffs: false,
    externalPayoffs: false,
    earlyPayoffs: false,
    feesHoldbacks: false,
    stipulations: false,
    commission: false,
    syndication: false,
  });

  const setComponentLoading = useCallback((componentName: string, isLoading: boolean) => {
    setLoadingStates((prev) => ({
      ...prev,
      [componentName]: isLoading,
    }));
  }, []);

  useEffect(() => {
    const fetchTags = async () => {
      try {
        setTagsLoading(true);
        const res = await get("integration/ocrolous/tags");
        if (res?.status === "success") {
          setAvailableTags(res.data || []);
        }
      } finally {
        setTagsLoading(false);
      }
    };

    fetchTags();
  }, []);

  useEffect(() => {
    if (Array.isArray(mediaAttachments)) {
      setFiles(mediaAttachments.filter((f) => f.category === "Bank Statements"));
    }
  }, [mediaAttachments]);

  useEffect(() => {
    fetchStatus();
  }, [dealApplicationId]);

  useEffect(() => {
    let interval;
    if (status === "processing") {
      interval = setInterval(fetchStatus, 15000);
    }
    return () => interval && clearInterval(interval);
  }, [status]);

  const fetchStatus = async () => {
    try {
      setStatus("loading");

      void fetchOcrolusSummary().catch(() => {});

      const res = await get(`integration/ocrolous/transactions/${dealApplicationId}`);

      if (res?.status === "not_found") {
        setStatus("not_found");
        setMessage(res.message);
        setTransactions([]);
        return;
      }

      if (res?.status === "processing") {
        setStatus("processing");
        setMessage(res.message);
        return;
      }

      if (res?.status === "success") {
        const txns = res?.data?.transactions || {};

        setTransactions(txns.enriched_transactions || []);
        setBankAccounts(txns.bank_accounts || {});
        setDebitsSummary(txns.fintech_debits_summary || {});
        setStatus("success");
        return;
      }

      setStatus("error");
      setMessage(res?.message || "Unable to load Ocrolus status");
    } catch {
      setStatus("error");
      setMessage("Network error while checking Ocrolus status");
    }
  };

  const fetchOcrolusSummary = async () => {
    try {
      setSummaryLoading(true);

      const res = await get(`/integration/ocrolous/book/${dealApplicationId}/summary`);

      if (res?.status === "success" || res?.data) {
        setOcrolusSummary(res.data);
      } else {
        setOcrolusSummary(null);
        setSanitizedPositions([]);
      }
    } finally {
      setSummaryLoading(false);
    }
  };

  const refreshOcrolusSilently = async () => {
    try {
      setSummaryLoading(true);

      const [txnsRes, summaryRes] = await Promise.all([
        get(`integration/ocrolous/transactions/${dealApplicationId}`),
        get(`/integration/ocrolous/book/${dealApplicationId}/summary`),
      ]);

      if (txnsRes?.status === "success") {
        const txns = txnsRes.data?.transactions || {};
        setTransactions(txns.enriched_transactions || []);
        setBankAccounts(txns.bank_accounts || {});
        setDebitsSummary(txns.fintech_debits_summary || {});
      }

      if (summaryRes?.status === "success" || summaryRes?.data) {
        setOcrolusSummary(summaryRes.data);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setSummaryLoading(false);
    }
  };

  const runOcrolus = async () => {
    if (!selectedFiles.length) {
      setMessage("Please select at least one bank statement.");
      return;
    }

    try {
      setIsRunning(true);
      setStatus("loading");

      await post(
        "integration/ocrolous/uploadApplicationStatements",
        {
          dealApplicationId,
          mediaAttachmentIds: selectedFiles,
        },
        { timeout: 0 }
      );

      setStatus("processing");
      setMessage("Ocrolus is processing the uploaded documents.");
    } catch {
      setStatus("error");
      setMessage("Failed to upload statements to Ocrolus.");
    } finally {
      setIsRunning(false);
      setShowModal(false);
      setSelectedFiles([]);
    }
  };

  if (status === "loading") {
    return <FullPageLoader />;
  }

  if (status === "processing") {
    return (
      <MDBox
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="400px"
        p={3}
        sx={{
          backgroundColor: "background.paper",
          borderRadius: 2,
          boxShadow: 1,
          background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
        }}
      >
        <MDBox position="relative" mb={3}>
          <MDBox
            sx={{
              width: 80,
              height: 80,
              border: "4px solid",
              borderColor: "grey.300",
              borderTopColor: "primary.main",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
              "@keyframes spin": {
                "0%": { transform: "rotate(0deg)" },
                "100%": { transform: "rotate(360deg)" },
              },
            }}
          />
          <MDBox
            position="absolute"
            top="50%"
            left="50%"
            sx={{ transform: "translate(-50%, -50%)" }}
          >
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
              <path
                d="M13 10V3L4 14h7v7l9-11h-7z"
                stroke="currentColor"
                strokeWidth={2}
                fill="currentColor"
              />
            </svg>
          </MDBox>
        </MDBox>

        <MDBox textAlign="center" mb={2}>
          <MDTypography
            variant="h4"
            gutterBottom
            sx={{ color: "text.primary", fontWeight: "bold" }}
          >
            Processing Your Statements
          </MDTypography>
          <MDTypography
            variant="body1"
            sx={{
              color: "text.secondary",
              maxWidth: "400px",
              lineHeight: 1.6,
            }}
          >
            Ocrolus is carefully analyzing your financial statements. This usually takes just a
            few moments.
          </MDTypography>
        </MDBox>

        <MDButton
          variant="contained"
          color="primary"
          onClick={() => fetchStatus()}
          startIcon={
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                stroke="currentColor"
                strokeWidth={2}
                fill="none"
              />
            </svg>
          }
          sx={{
            px: 3,
            py: 1.5,
            borderRadius: 2,
            textTransform: "none",
            fontWeight: "bold",
            boxShadow: 2,
            "&:hover": {
              boxShadow: 4,
              transform: "translateY(-1px)",
            },
            transition: "all 0.2s ease-in-out",
          }}
        >
          Check Now
        </MDButton>

        <MDTypography variant="caption" sx={{ color: "text.secondary", mt: 2 }}>
          Last checked: {new Date().toLocaleTimeString()}
        </MDTypography>
      </MDBox>
    );
  }

  return (
    <MDBox>
      <MDBox display="flex" justifyContent="flex-end" mr={3}>
        {status === "not_found" && StorageUtils.isFeatureEnabled("ocrolus") && (
          <MDButton
            variant="contained"
            color="primary"
            onClick={() => setShowModal(true)}
            disabled={isRunning}
            sx={{
              px: 3,
              py: 1,
              marginTop: "20px",
              borderRadius: 1,
              textTransform: "none",
              fontWeight: "bold",
              boxShadow: 2,
              "&:hover": {
                boxShadow: 3,
              },
            }}
            startIcon={
              isRunning ? (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M12 2v4m0 12v4M4.93 4.93l2.83 2.83m8.48 8.48l2.83 2.83M2 12h4m12 0h4M4.93 19.07l2.83-2.83m8.48-8.48l2.83-2.83"
                    stroke="currentColor"
                    strokeWidth={2}
                    fill="none"
                  />
                </svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
                    stroke="currentColor"
                    strokeWidth={2}
                    fill="currentColor"
                  />
                  <path
                    d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    stroke="currentColor"
                    strokeWidth={2}
                    fill="none"
                  />
                </svg>
              )
            }
          >
            {isRunning ? "Starting..." : "Run Ocrolus"}
          </MDButton>
        )}
      </MDBox>

      {status === "error" && <MDTypography color="error.main">{message}</MDTypography>}

      {status === "success" && (
        <>
          <OcrolusSummaryCard
            dealApplicationId={dealApplicationId}
            allowButton={false}
            allowSummarySelection={false}
            allowResubmit={StorageUtils.isFeatureEnabled("ocrolus")}
            onResubmit={() => setShowModal(true)}
            loading={summaryLoading}
          />

          <OcrolusDetectSignals dealApplicationId={dealApplicationId} />

          <OcrolusStatementsSummary
            dealApplicationId={dealApplicationId}
            setComponentLoading={setComponentLoading}
            bankAccounts={bankAccounts}
            debits={debitsSummary}
            summaryMonths={ocrolusSummary?.months || {}}
            loading={summaryLoading}
          />

          <OcrolusAdvancesPositions
            advances={ocrolusSummary?.advances || []}
            setPositionCount={setPositionCount}
            loading={summaryLoading}
          />

          <OcrolusRecurringPayments
            dealApplicationId={dealApplicationId}
            setComponentLoading={setComponentLoading}
            recurringDebits={ocrolusSummary?.recurringDebits || []}
            loading={summaryLoading}
          />

          <OcrolusTransactions
            dealApplicationId={dealApplicationId}
            transactions={transactions}
            availableTags={availableTags}
            bankAccounts={bankAccounts}
            loading={transactionsLoading}
            onRefresh={refreshOcrolusSilently}
            setTransactions={setTransactions}
          />
        </>
      )}

      <CustomModal
        open={showModal}
        onClose={() =>{
            setShowModal(false); 
            setSelectedFiles([])}
        }
        onCancel={() =>{
          setShowModal(false); 
          setSelectedFiles([])}
      }
        title="Upload Bank Statements (Ocrolus)"
        submitText="Submit"
        onSubmit={runOcrolus}
        maxWidth="sm"
        fullWidth
      >
        <Autocomplete
          multiple
          options={files.map((f) => ({ value: f.id, label: f.name }))}
          value={files
            .filter((f) => selectedFiles.includes(f.id))
            .map((f) => ({ value: f.id, label: f.name }))}
          onChange={(_, v) => setSelectedFiles(v.map((x) => x.value))}
          getOptionLabel={(o) => o.label}
          renderInput={(params) => <TextField {...params} label="Select Bank Statement Files" />}
        />
      </CustomModal>
    </MDBox>
  );
}
